import React from 'react'

import { useFreeBuyBonusStore, useBuyBonusStore } from '@stores'

import { BonusBundleTemplate } from '@components/BonusSystemCommon'
import { BonusBundleStyles } from '@components/BonusSystemCommon/content/BonusBundleTemplate'

import { buildAtlasImageStyles } from '@utils/buildAtlasImageStyles'

import { Bonus } from 'game-ui-types'
import { useTranslation } from '@amusnet/translations/react'
import { useLoadAtlasesWhenGameSettingsReady } from '@hooks'
import { getBuyBonusAtlasPaths } from '@utils/gameSettings'

interface FBBMultipleBonusesProps {
    setSelectedBonus: (bonus: Bonus) => void
}

const FBBMultipleBonuses: React.FC<FBBMultipleBonusesProps> = ({ setSelectedBonus }: FBBMultipleBonusesProps) => {
    const bonuses = useFreeBuyBonusStore((state) => state.bonuses)
    const styles = useFreeBuyBonusStore((state) => state.styles)
    const declinedBonusesIds = useFreeBuyBonusStore((state) => state.declinedBonusesIds)
    const bundleImageName = useFreeBuyBonusStore((state) => state.bundleImageName)
    const setActiveBonus = useFreeBuyBonusStore((state) => state.setActiveBonus)
    const setBonusSystemState = useFreeBuyBonusStore((state) => state.setBonusSystemState)
    const setBonusSystemDeclineState = useFreeBuyBonusStore((state) => state.setBonusSystemDeclineState)
    const setHasActivatedBonus = useFreeBuyBonusStore((state) => state.setHasActivatedBonus)

    const findElementByImageName = useBuyBonusStore((state) => state.findElementByImageName)
    const atlasMap = useBuyBonusStore((state) => state.atlasMap)
    const cachedImages = useBuyBonusStore((state) => state.cachedImages)
    const loadAtlasesFromPath = useBuyBonusStore((state) => state.loadAtlasesFromPath)
    const buyBonusConfig = useBuyBonusStore((state) => state.buyBonusConfig)

    const { t } = useTranslation()

    useLoadAtlasesWhenGameSettingsReady(getBuyBonusAtlasPaths, loadAtlasesFromPath)

    const image = atlasMap && findElementByImageName(bundleImageName)

    const imageStyles = image
        ? buildAtlasImageStyles(image.atlasName, image.value, cachedImages[image.atlasName])
        : undefined

    const bonusBundleStyles: BonusBundleStyles = {
        primaryTextColor: buyBonusConfig.layout.symbolLabelColor?.length
            ? buyBonusConfig.layout.symbolLabelColor
            : styles.primaryTextColor,
        secondaryTextColorBundle: styles.secondaryTextColorBundle,
        playBtnColor: styles.playBtnColor,
        playLaterBtnColor: styles.playLaterBtnColor,
        declineBtnColor: styles.declineBtnColor,
        border: '#FFA146 2px solid',
        background: styles.bundleBackground.backgroundImage!,
    }

    return (
        <div className="flex flex-col items-center justify-between">
            <div className="flex flex-col w-full">
                <div style={imageStyles} />

                <div
                    style={{ ...styles.bodyBackground }}
                    className="flex flex-col justify-center items-center w-full mt-[-3px] py-[15px] h-[384px]"
                >
                    <div
                        style={{ ...styles.bundleContainerBackground }}
                        className="w-[650px] max-h-[355px] flex flex-col items-center pt-[10px] overflow-x-hidden rounded-[8px] bg-black touch-auto"
                    >
                        {bonuses.map((bonus, index) => (
                            <BonusBundleTemplate
                                elementId={`bonus-${bonus.id}`}
                                key={index}
                                bonus={bonus}
                                bundleName={t('gameui.bonusSystem.freeBonusName', {
                                    bonusName: bonus.typeConfiguration?.freeBuyBonusConfiguration?.label ?? '',
                                })}
                                setHasActivatedBonus={setHasActivatedBonus}
                                setSelectedBonus={setSelectedBonus}
                                declinedBonusesIds={declinedBonusesIds}
                                styles={bonusBundleStyles}
                                setActiveBonus={setActiveBonus}
                                setBonusSystemState={setBonusSystemState}
                                setBonusSystemDeclineState={setBonusSystemDeclineState}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default FBBMultipleBonuses
